import { RowData } from '../App';
import type { ApiBlock } from '../utils/apiTypes';

export type SbsBlocksOption = 'normal' | 'left' | 'right';

type BlockDefaults = {
    shouldDisplayBlockName: boolean;
    blockNameFont: string;
    blockNameSize: number;
    blockNameWeight: number;
    blockNameColor: string;
    numColumns: number;
    distanceBelowBlockName: number;
    labelValueGap: number;
    marginColumn: number;
    edgeDistance: number;
    distanceBetweenRows: number;
    distanceFromPreviousBlock: number;
    isSongBlock: boolean;
    isSingleCell: boolean;
    isCreditsList: boolean;
    separator: string;
    isLogoBlock: boolean;
    sbsBlocks: SbsBlocksOption;
    sbsDistance: number;
    labelFontName: string;
    labelFontSize: number;
    labelFontWeight: number;
    labelAlign: string;
    labelVerticalAlign: string;
    labelFontColor: string;
    valueFontName: string;
    valueFontSize: number;
    valueFontWeight: number;
    valueAlign: string;
    valueVerticalAlign: string;
    valueFontColor: string;
    hasSeparateValueFormatting: boolean;
    isLocked: boolean;
    dataRows: RowData[];
};

export class Block {
    static readonly defaults: BlockDefaults = {
        shouldDisplayBlockName: true,
        blockNameFont: 'Arial',
        blockNameSize: 30,
        blockNameWeight: 400,
        blockNameColor: '#000000',
        numColumns: 1,
        distanceBelowBlockName: 10,
        labelValueGap: 5,
        marginColumn: 45,
        edgeDistance: 0,
        distanceBetweenRows: 1,
        distanceFromPreviousBlock: 10,
        isSongBlock: false,
        isSingleCell: false,
        isCreditsList: false,
        separator: '•',
        isLogoBlock: false,
        sbsBlocks: 'normal',
        sbsDistance: 0,
        labelFontName: 'Arial',
        labelFontSize: 11,
        labelFontWeight: 400,
        labelAlign: 'right',
        labelVerticalAlign: 'bottom',
        labelFontColor: '#000000',
        valueFontName: 'Arial',
        valueFontSize: 11,
        valueFontWeight: 400,
        valueAlign: 'left',
        valueVerticalAlign: 'bottom',
        valueFontColor: '#000000',
        hasSeparateValueFormatting: false,
        isLocked: false,
        dataRows: [] as RowData[],
    };

    static normalizeSbsBlocks(
        value: unknown,
        fallback: SbsBlocksOption = Block.defaults.sbsBlocks
    ): SbsBlocksOption {
        if (typeof value !== 'string') return fallback;
        const normalized: string = value.trim().toLowerCase();
        if (normalized === 'left') return 'left';
        if (normalized === 'right') return 'right';
        if (normalized === 'normal') return 'normal';
        return fallback;
    }

    static create(init: { name: string } & Partial<BlockDefaults>): Block {
        const merged: BlockDefaults = {
            ...Block.defaults,
            ...init,
            dataRows: init.dataRows ?? Block.defaults.dataRows,
            sbsBlocks: Block.normalizeSbsBlocks(init.sbsBlocks ?? Block.defaults.sbsBlocks),
        };
        return new Block(
            init.name,
            merged.shouldDisplayBlockName,
            merged.blockNameFont,
            merged.blockNameSize,
            merged.blockNameWeight,
            merged.blockNameColor,
            merged.numColumns,
            merged.distanceBelowBlockName,
            merged.labelValueGap,
            merged.marginColumn,
            merged.edgeDistance,
            merged.distanceBetweenRows,
            merged.distanceFromPreviousBlock,
            merged.isSongBlock,
            merged.isSingleCell,
            merged.isCreditsList,
            merged.separator,
            merged.isLogoBlock,
            merged.sbsBlocks,
            merged.sbsDistance,
            merged.labelFontName,
            merged.labelFontSize,
            merged.labelFontWeight,
            merged.labelAlign,
            merged.labelVerticalAlign,
            merged.labelFontColor,
            merged.valueFontName,
            merged.valueFontSize,
            merged.valueFontWeight,
            merged.valueAlign,
            merged.valueVerticalAlign,
            merged.valueFontColor,
            merged.hasSeparateValueFormatting,
            merged.isLocked,
            merged.dataRows
        );
    }

    private getApiProps(overrideColors: boolean): ApiBlock['properties'] {
        return {
            name: this.name,
            show_block_name: this.shouldDisplayBlockName,
            block_name_font_style: this.blockNameFont,
            block_name_font_size: this.blockNameSize,
            block_name_font_weight: this.blockNameWeight,
            block_name_color: overrideColors ? '#000000' : this.blockNameColor,
            column_num: this.numColumns,
            distance_below_block_name: this.distanceBelowBlockName,
            distance_between_columns: this.marginColumn,
            value_label_gap: this.labelValueGap,
            distance_between_rows: this.distanceBetweenRows,
            distance_from_previous: this.distanceFromPreviousBlock,
            song_block: this.isSongBlock,
            single_cell: this.isSingleCell,
            credits_list: this.isCreditsList,
            credits_list_separator: this.separator,
            logo_block: this.isLogoBlock,
            sbs_blocks: this.sbsBlocks,
            sbs_distance: this.sbsDistance,
            label_font_name: this.labelFontName,
            label_font_size: this.labelFontSize,
            label_font_weight: this.labelFontWeight,
            label_align: this.labelAlign,
            label_vertical_align: this.labelVerticalAlign,
            label_color: overrideColors ? '#000000' : this.labelFontColor,
            value_font_name: this.valueFontName,
            value_font_size: this.valueFontSize,
            value_font_weight: this.valueFontWeight,
            value_align: this.valueAlign,
            value_vertical_align: this.valueVerticalAlign,
            value_color: overrideColors ? '#000000' : this.valueFontColor,
            has_separate_value_formatting: this.hasSeparateValueFormatting,
            block_locked: this.isLocked,
            margin_column: this.marginColumn,
            edge_distance: this.edgeDistance,
            label_value_gap: this.labelValueGap,
        };
    }

    toRenderApiProperties(overrideColors: boolean = false): ApiBlock['properties'] {
        return this.getApiProps(overrideColors);
    }

    toPreviewApiProperties(): ApiBlock['properties'] {
        return this.getApiProps(false);
    }

    constructor(
        public name: string,
        public shouldDisplayBlockName: boolean,
        public blockNameFont: string,
        public blockNameSize: number,
        public blockNameWeight: number,
        public blockNameColor: string,
        public numColumns: number,
        public distanceBelowBlockName: number,
        public labelValueGap: number,
        public marginColumn: number,
        public edgeDistance: number,
        public distanceBetweenRows: number,
        public distanceFromPreviousBlock: number,
        public isSongBlock: boolean,
        public isSingleCell: boolean,
        public isCreditsList: boolean,
        public separator: string,
        public isLogoBlock: boolean,
        public sbsBlocks: SbsBlocksOption,
        public sbsDistance: number,
        public labelFontName: string,
        public labelFontSize: number,
        public labelFontWeight: number,
        public labelAlign: string,
        public labelVerticalAlign: string,
        public labelFontColor: string,
        public valueFontName: string,
        public valueFontSize: number,
        public valueFontWeight: number,
        public valueAlign: string,
        public valueVerticalAlign: string,
        public valueFontColor: string,
        public hasSeparateValueFormatting: boolean = false,
        public isLocked: boolean = false,
        public dataRows: RowData[] = []
    ) {}
}

